import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  Schedule,
  ScheduleCustomField,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useMemo, useCallback } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useCustomFieldsEditor } from '@sequencehq/core-components'

type FieldConfig<
  TargetDataStructure extends Record<string, unknown>,
  Property extends keyof TargetDataStructure = keyof TargetDataStructure
> = {
  disabled: boolean
  validationErrors: ValidationResult[]
  value: TargetDataStructure[Property]
  onChange: (newValue: TargetDataStructure[Property]) => void
}

type UseAdditionalInformationWidget = () => {
  fieldsConfig: {
    label: FieldConfig<Schedule, 'label'> & {
      hidden: boolean
    }
    taxRate: {
      options: {
        id: string
        label: string
        value: string
      }[]
      value: string
      onChange: (newTaxRateId: string) => void
      disabled: boolean
      validationErrors: ValidationResult[]
      hidden: boolean
    }
    customFields: {
      value: ScheduleCustomField[]
      keyOptions: Array<{ id: string; label: string; value: string }>
      onAdd: () => void
      onUpdate: (id: string, change: Partial<ScheduleCustomField>) => void
      onRemove: (id: string) => void
      disabled: boolean
    }
  }
}

export const useAdditionalInformationWidget: UseAdditionalInformationWidget =
  () => {
    const cubeContext = useCubeContext()

    const customFieldKeysQuery = useQuery(
      dashboard20240730Client.getBillingScheduleCustomFieldKeys
    )

    const customFields = cubeContext.queries.rawData.data.schedule.customFields

    const updateCustomFields = useCallback(
      (next: ScheduleCustomField[]) => {
        cubeContext.mutators.updateData({
          schedule: {
            customFields: next
          }
        })
      },
      [cubeContext.mutators]
    )

    const customFieldsEditor = useCustomFieldsEditor({
      value: customFields,
      onChange: updateCustomFields,
      availableKeys: customFieldKeysQuery.data?.items ?? []
    })

    const customFieldsConfig = useMemo(
      () => ({
        value: customFields,
        disabled: false,
        ...customFieldsEditor
      }),
      [customFields, customFieldsEditor]
    )

    const taxRateOptions = useMemo(() => {
      return Object.values(cubeContext.queries.availableTaxRates).map(
        taxRate => ({
          id: taxRate.id,
          label: taxRate.name,
          value: taxRate.id
        })
      )
    }, [cubeContext.queries.availableTaxRates])

    const fieldsConfig = useMemo(() => {
      return {
        label: {
          hidden:
            !cubeContext.queries.availableFeatures.schedule.label.available
              .visible,
          value: cubeContext.queries.rawData.data.schedule.label,
          onChange: (newValue: string) =>
            cubeContext.mutators.updateData({
              schedule: {
                label: newValue
              }
            }),
          validationErrors: [],
          disabled:
            !cubeContext.queries.availableFeatures.schedule.label.available
              .enabled
        },
        taxRate: {
          options: taxRateOptions,
          value: cubeContext.queries.rawData.data.schedule.taxRateId,
          onChange: (newTaxRate: string) =>
            cubeContext.mutators.updateData({
              schedule: {
                taxRateId: newTaxRate
              }
            }),
          disabled:
            !cubeContext.queries.availableFeatures.schedule.taxRate.available
              .enabled,
          validationErrors:
            cubeContext.queries.validation.activeValidationResults?.schedule
              .taxRate ?? [],
          hidden:
            !cubeContext.queries.availableFeatures.schedule.taxRate.available
              .visible
        },
        customFields: customFieldsConfig
      }
    }, [cubeContext, taxRateOptions, customFieldsConfig])

    return {
      fieldsConfig
    }
  }
